// src/pages/gt3x/Drivers.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchSheetViaProxy } from "../../lib/sheets";

// drv_profile enthält u.a.:
// car_number, driver_name, driver_id, class, country_code, team_name,
// team_logo_url, driver_image_url, car_name, status, current_points, current_position,
// ... is_active, profile_updated_at, flag_url (neu)
const RANGE = "drv_profile!A1:AI1000";

function flagEmoji(code) {
    const c = String(code ?? "").trim().toUpperCase();
    if (c.length !== 2) return "🏁";
    return c
        .split("")
        .map((ch) => String.fromCodePoint(127397 + ch.charCodeAt()))
        .join("");
}

export default function Drivers() {
    const nav = useNavigate();

    const [data, setData] = useState({ header: [], rows: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const res = await fetchSheetViaProxy({ range: RANGE });
                setData(res);
            } catch (e) {
                setError(e?.message || "Fehler beim Laden der Fahrer.");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const drivers = useMemo(() => {
        const rows = data.rows || [];

        const normalizeBool = (v) => {
            if (v == null) return true; // default: aktiv
            const s = String(v).trim().toLowerCase();
            if (s === "" || s === "1" || s === "true" || s === "yes" || s === "ja") return true;
            if (s === "0" || s === "false" || s === "no" || s === "nein") return false;
            return true;
        };

        return rows
            .map((r) => ({
                driver_id: String(r.driver_id ?? "").trim(),
                driver_name: String(r.driver_name ?? "").trim(),
                car_number: String(r.car_number ?? "").trim(),

                class: String(r.class ?? "").trim(),
                country_code: String(r.country_code ?? "").trim(),
                flag_url: String(r.flag_url ?? "").trim(), // ✅ neu

                team_name: String(r.team_name ?? "").trim(),
                team_logo_url: String(r.team_logo_url ?? "").trim(),
                driver_image_url: String(r.driver_image_url ?? "").trim(),

                status: String(r.status ?? "").trim(),
                is_active: normalizeBool(r.is_active),

                current_points: r.current_points,
                current_position: r.current_position,
                profile_updated_at: String(r.profile_updated_at ?? "").trim(),
            }))
            .filter((d) => d.driver_id && d.driver_name)
            // ✅ wenn du ALLE anzeigen willst: nächste Zeile auskommentieren
            .filter((d) => d.is_active)
            .sort((a, b) => a.driver_name.localeCompare(b.driver_name, "de"));
    }, [data]);

    return (
        <>
            <div className="card overview-card">
                <h2>Fahrer</h2>
                <p className="muted">Wähle einen Fahrer aus, um das Profil zu öffnen.</p>
            </div>

            {loading && (
                <div className="card overview-card">
                    <p>Lade Fahrer …</p>
                </div>
            )}

            {!loading && error && (
                <div className="card overview-card">
                    <p className="muted" style={{ color: "#f55" }}>
                        {error}
                    </p>
                </div>
            )}

            {!loading && !error && (
                <div className="card overview-card">
                    {drivers.length === 0 ? (
                        <p className="muted">Keine Fahrer gefunden.</p>
                    ) : (
                        <div className="rv-driverGrid">
                            {drivers.map((d) => (
                                <button
                                    key={d.driver_id}
                                    type="button"
                                    className="rv-driverTile"
                                    onClick={() => nav(`/gt3x/fahrer/${encodeURIComponent(d.driver_id)}`)}
                                    title={`${d.driver_name}${d.team_name ? ` • ${d.team_name}` : ""}`}
                                >
                                    <div className="rv-driverTile__avatar">
                                        {d.driver_image_url ? (
                                            <img src={d.driver_image_url} alt={d.driver_name} loading="lazy" />
                                        ) : (
                                            <div className="rv-driverTile__avatarFallback" />
                                        )}
                                    </div>

                                    <div className="rv-driverTile__body">
                                        {/* Zeile 1: Name (2 Zeilen erlaubt) + Zeile 2: Badges */}
                                        <div className="rv-driverTile__top">
                                            <div className="rv-driverTile__name">{d.driver_name}</div>

                                            <div className="rv-driverTile__badges">
                                                {/* Flag */}
                                                {d.flag_url ? (
                                                    <img
                                                        className="rv-driverIcon"
                                                        src={d.flag_url}
                                                        alt={d.country_code || "Flag"}
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <span className="rv-driverBadge">{flagEmoji(d.country_code)}</span>
                                                )}

                                                {/* Startnummer */}
                                                {d.car_number ? <span className="rv-driverBadge">#{d.car_number}</span> : null}

                                                {/* Class Text (PRO/AM) */}
                                                {d.class ? (
                                                    <span
                                                        className={`rv-classTag ${
                                                            String(d.class).toUpperCase() === "PRO"
                                                                ? "is-pro"
                                                                : String(d.class).toUpperCase() === "INT"
                                                                    ? "is-int"
                                                                    : "is-am"
                                                        }`}
                                                    >
                                                    {String(d.class).toUpperCase()}
                                                  </span>
                                                ) : null}
                                            </div>
                                        </div>

                                        {/* Zeile 3: Team */}
                                        <div className="rv-driverTile__teamRow">
                                            <span className="rv-driverTile__team">{d.team_name || "–"}</span>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </>
    );
}